import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import {
  StyleSheet,
  View,
  TouchableOpacity,
  ScrollView,
  Platform,
  Dimensions,
  ActivityIndicator,
  PanResponder,
  GestureResponderEvent,
  PanResponderGestureState,
  type StyleProp,
  type ViewStyle,
} from "react-native";
import Svg, { G, Rect, Circle, Line, Text as SvgText, Defs, Pattern, Path } from "react-native-svg";
import { Ionicons } from "@expo/vector-icons";
import { ThemedText } from "@/components/ThemedText";
import { ThemedButton } from "@/components/ThemedButton";
import { EntityIcon } from "@/components/ui/EntityIcon";
import { useThemeColor } from "@/hooks/useThemeColor";
import { MemoryModel, MediaModel, Contact, ContactModel, GroupModel } from "common247";
import { MemoryFilter } from "./MemoryFilter";
import { MemoryExporter } from "./MemoryExporter";
import {
  IFilteredEntityResultSet,
  IMemoryFilterCriteria,
  applyMemoryFilter,
  calculateMemoryDurationDays,
  calculateMemoryTimeDifferenceDays,
} from "@/services/memory-filter-service";
import {
  IVisualiserNode,
  IVisualiserLink,
  buildVisualiserGraph,
  runVisualiserSimulation,
  generateVisualiserSvgString,
} from "@/services/visualiser-layout-service";
import {
  exportVisualiserToPdf,
  exportVisualiserToImage,
} from "@/services/memory-export-service";

export interface IVisualiserProps {
  /** Master source memories */
  allMemories: MemoryModel[];
  /** Master source media items */
  allMedia?: MediaModel[];
  /** Master source contacts */
  allContacts?: (Contact | ContactModel)[];
  /** Master source groups */
  allGroups?: GroupModel[];
  /** Callback when user selects a memory node */
  onSelectMemory?: (memory: MemoryModel) => void;
  /** Callback when user selects a media node */
  onSelectMedia?: (media: MediaModel) => void;
  /** Optional container style */
  style?: StyleProp<ViewStyle>;
  /** Optional initial filter criteria */
  initialCriteria?: IMemoryFilterCriteria;
  /** Optional canvas dimensions override */
  canvasWidth?: number;
  canvasHeight?: number;
}

/**
 * Archival Heirloom Visualiser Component.
 * Visualizes user memories, media, contacts, and groups using D3 force simulation:
 * - Memory block dimension scales with duration in days.
 * - Media dot radius scales with FavouriteRating (1 smallest to 5 biggest).
 * - Distance between memories scales with chronological delta in days.
 * - Media, Contacts, and Groups are positioned in the space between memories.
 * - Interactive canvas with drag-to-pan, scroll/button zoom, reset & fit.
 * - Integrated MemoryFilter for dynamic cross-filtering.
 * - One-click export to PDF and high-resolution Image.
 */
export function Visualiser({
  allMemories = [],
  allMedia = [],
  allContacts = [],
  allGroups = [],
  onSelectMemory,
  onSelectMedia,
  style,
  initialCriteria,
  canvasWidth = 1000,
  canvasHeight = 700,
}: IVisualiserProps): React.JSX.Element {
  const [filteredResults, setFilteredResults] = useState<IFilteredEntityResultSet>(() =>
    applyMemoryFilter(allMemories, allMedia, allContacts, allGroups, initialCriteria)
  );

  const [nodes, setNodes] = useState<IVisualiserNode[]>([]);
  const [links, setLinks] = useState<IVisualiserLink[]>([]);
  const [selectedNode, setSelectedNode] = useState<IVisualiserNode | null>(null);
  const [isSimulating, setIsSimulating] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  // Pan and Zoom Transformation State
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1.0);

  // Pan tracking references
  const panStartRef = useRef({ x: 0, y: 0 });
  const isDraggingRef = useRef(false);

  // Theme tokens
  const bgColor = useThemeColor({}, "background");
  const cardBg = useThemeColor({}, "card");
  const borderColor = useThemeColor({}, "border");
  const tintColor = useThemeColor({}, "tint");
  const textMuted = useThemeColor({}, "textMuted");

  /**
   * Recompute D3 graph layout whenever filtered results change.
   */
  useEffect(() => {
    setIsSimulating(true);
    // Yield to UI thread before heavy layout calculation
    const timer = setTimeout(() => {
      try {
        const rawGraph = buildVisualiserGraph(filteredResults, canvasWidth, canvasHeight);
        const layoutGraph = runVisualiserSimulation(rawGraph.nodes, rawGraph.links, {
          width: canvasWidth,
          height: canvasHeight,
          ticks: 120,
        });

        setNodes(layoutGraph.nodes);
        setLinks(layoutGraph.links);

        // If previously selected node is no longer in filtered set, clear selection
        if (selectedNode && !layoutGraph.nodes.some((n) => n.id === selectedNode.id)) {
          setSelectedNode(null);
        }
      } finally {
        setIsSimulating(false);
      }
    }, 10);

    return () => clearTimeout(timer);
  }, [filteredResults, canvasWidth, canvasHeight]);

  /**
   * PanResponder for smooth drag-to-pan interaction on both mobile and web.
   */
  const panResponder = useMemo(
    () =>
      PanResponder.create({
        onStartShouldSetPanResponder: () => true,
        onMoveShouldSetPanResponder: (_, gesture) =>
          Math.abs(gesture.dx) > 2 || Math.abs(gesture.dy) > 2,
        onPanResponderGrant: () => {
          panStartRef.current = { ...pan };
          isDraggingRef.current = true;
        },
        onPanResponderMove: (_, gesture: PanResponderGestureState) => {
          setPan({
            x: panStartRef.current.x + gesture.dx,
            y: panStartRef.current.y + gesture.dy,
          });
        },
        onPanResponderRelease: () => {
          isDraggingRef.current = false;
        },
        onPanResponderTerminate: () => {
          isDraggingRef.current = false;
        },
      }),
    [pan]
  );

  /**
   * Zoom Actions
   */
  const handleZoomIn = () => {
    setZoom((prev) => Math.min(3.0, prev * 1.25));
  };

  const handleZoomOut = () => {
    setZoom((prev) => Math.max(0.3, prev / 1.25));
  };

  const handleResetView = () => {
    setPan({ x: 0, y: 0 });
    setZoom(1.0);
  };

  const handleFitToContent = () => {
    if (nodes.length === 0) {
      handleResetView();
      return;
    }

    let minX = Infinity;
    let maxX = -Infinity;
    let minY = Infinity;
    let maxY = -Infinity;

    nodes.forEach((n) => {
      const x = n.x ?? canvasWidth / 2;
      const y = n.y ?? canvasHeight / 2;
      if (x < minX) minX = x;
      if (x > maxX) maxX = x;
      if (y < minY) minY = y;
      if (y > maxY) maxY = y;
    });

    const padding = 80;
    const graphW = Math.max(100, maxX - minX + padding * 2);
    const graphH = Math.max(100, maxY - minY + padding * 2);

    const scaleX = canvasWidth / graphW;
    const scaleY = canvasHeight / graphH;
    const nextZoom = Math.min(2.0, Math.max(0.4, Math.min(scaleX, scaleY)));

    const midX = (minX + maxX) / 2;
    const midY = (minY + maxY) / 2;

    setZoom(nextZoom);
    setPan({
      x: (canvasWidth / 2 - midX) * nextZoom,
      y: (canvasHeight / 2 - midY) * nextZoom,
    });
  };

  /**
   * PDF Export Handler
   */
  const handleExportPdf = async () => {
    if (isExporting) return;
    setIsExporting(true);
    try {
      const svgMarkup = generateVisualiserSvgString(nodes, links, canvasWidth, canvasHeight);
      await exportVisualiserToPdf(filteredResults, svgMarkup);
    } catch (err) {
      console.error("[Visualiser] Failed to export PDF:", err);
    } finally {
      setIsExporting(false);
    }
  };

  /**
   * Image Export Handler
   */
  const handleExportImage = async () => {
    if (isExporting) return;
    setIsExporting(true);
    try {
      await exportVisualiserToImage("memories-visualiser-svg");
    } catch (err) {
      console.error("[Visualiser] Failed to export Image:", err);
    } finally {
      setIsExporting(false);
    }
  };

  /**
   * Node Click Handler
   */
  const handleNodePress = (node: IVisualiserNode) => {
    setSelectedNode(node);
    if (node.type === "memory" && onSelectMemory) {
      onSelectMemory(node.data as MemoryModel);
    } else if (node.type === "media" && onSelectMedia) {
      onSelectMedia(node.data as MediaModel);
    }
  };

  return (
    <View style={[styles.container, { backgroundColor: bgColor }, style]}>
      {/* 1. Header Filter Integration */}
      <MemoryFilter
        title="Filter Graph"
        allMemories={allMemories}
        allMedia={allMedia}
        allContacts={allContacts}
        allGroups={allGroups}
        initialCriteria={initialCriteria}
        onFilteredResultsChange={setFilteredResults}
      />

      {/* Visualiser Action Toolbar / Exporter */}
      <View style={styles.exporterToolbar}>
        <MemoryExporter
          data={filteredResults}
          onExportPdf={handleExportPdf}
          onExportImage={handleExportImage}
        />
      </View>

      {/* 2. Visualiser Canvas Viewport */}
      <View style={[styles.canvasWrapper, { borderColor }]}>
        {/* Floating Zoom & Canvas Controls */}
        <View style={styles.controlsOverlay}>
          <TouchableOpacity
            style={[styles.controlBtn, { backgroundColor: cardBg, borderColor }]}
            onPress={handleZoomIn}
            accessibilityLabel="Zoom In"
          >
            <Ionicons name="add" size={18} color="#1E293B" />
          </TouchableOpacity>
          <TouchableOpacity
            style={[styles.controlBtn, { backgroundColor: cardBg, borderColor }]}
            onPress={handleZoomOut}
            accessibilityLabel="Zoom Out"
          >
            <Ionicons name="remove" size={18} color="#1E293B" />
          </TouchableOpacity>
          <TouchableOpacity
            style={[styles.controlBtn, { backgroundColor: cardBg, borderColor }]}
            onPress={handleResetView}
            accessibilityLabel="Reset View"
          >
            <Ionicons name="scan-outline" size={16} color="#1E293B" />
          </TouchableOpacity>
          <TouchableOpacity
            style={[styles.controlBtn, { backgroundColor: cardBg, borderColor }]}
            onPress={handleFitToContent}
            accessibilityLabel="Fit to Screen"
          >
            <Ionicons name="expand-outline" size={16} color="#1E293B" />
          </TouchableOpacity>
        </View>

        {/* Legend Overlay */}
        <View style={[styles.legendOverlay, { backgroundColor: cardBg, borderColor }]}>
          <View style={styles.legendItem}>
            <View style={styles.legendMemorySquare} />
            <ThemedText style={styles.legendText}>Memory (Days)</ThemedText>
          </View>
          <View style={styles.legendItem}>
            <View style={styles.legendMediaDot} />
            <ThemedText style={styles.legendText}>Media (Rating 1-5)</ThemedText>
          </View>
          <View style={styles.legendItem}>
            <View style={styles.legendContactDot} />
            <ThemedText style={styles.legendText}>Contact</ThemedText>
          </View>
          <View style={styles.legendItem}>
            <View style={styles.legendGroupDot} />
            <ThemedText style={styles.legendText}>Group</ThemedText>
          </View>
        </View>

        {/* Simulation Indicator */}
        {isSimulating && (
          <View style={styles.loadingOverlay}>
            <ActivityIndicator size="small" color="#2563EB" />
            <ThemedText style={styles.loadingText}>Synthesizing Relationships...</ThemedText>
          </View>
        )}

        {/* Interactive SVG Canvas */}
        <View style={styles.svgContainer} {...panResponder.panHandlers}>
          <Svg
            id="memories-visualiser-svg"
            width="100%"
            height="100%"
            viewBox={`0 0 ${canvasWidth} ${canvasHeight}`}
            style={styles.svgElement}
          >
            <Defs>
              <Pattern id="gridPattern" width="40" height="40" patternUnits="userSpaceOnUse">
                <Path d="M 40 0 L 0 0 0 40" fill="none" stroke="#E2E8F0" strokeWidth="0.5" />
              </Pattern>
            </Defs>

            {/* Background Grid */}
            <Rect width="100%" height="100%" fill="url(#gridPattern)" />

            {/* Transform Group with Pan and Zoom */}
            <G transform={`translate(${pan.x}, ${pan.y}) scale(${zoom})`}>
              {/* Layer 1: Links */}
              <G id="visualiser-links">
                {links.map((link) => {
                  const src =
                    typeof link.source === "object"
                      ? link.source
                      : nodes.find((n) => n.id === link.source);
                  const tgt =
                    typeof link.target === "object"
                      ? link.target
                      : nodes.find((n) => n.id === link.target);

                  if (!src || !tgt || src.x === undefined || tgt.x === undefined) {
                    return null;
                  }

                  const isSequence = link.type === "memory-sequence";
                  const stroke = isSequence ? "#94A3B8" : "#CBD5E1";
                  const strokeWidth = isSequence ? 2.5 : 1.2;
                  const strokeDasharray = isSequence ? undefined : "3,3";

                  return (
                    <Line
                      key={link.id}
                      x1={src.x}
                      y1={src.y}
                      x2={tgt.x}
                      y2={tgt.y}
                      stroke={stroke}
                      strokeWidth={strokeWidth}
                      strokeDasharray={strokeDasharray}
                    />
                  );
                })}
              </G>

              {/* Layer 2: Nodes */}
              <G id="visualiser-nodes">
                {nodes.map((node) => {
                  const x = node.x ?? canvasWidth / 2;
                  const y = node.y ?? canvasHeight / 2;
                  const isSelected = selectedNode?.id === node.id;

                  if (node.type === "memory") {
                    const half = node.size / 2;
                    return (
                      <G
                        key={node.id}
                        transform={`translate(${x - half}, ${y - half})`}
                        onPress={() => handleNodePress(node)}
                      >
                        {/* Heirloom Memory Block */}
                        <Rect
                          width={node.size}
                          height={node.size}
                          fill="#F5EBE1"
                          stroke={isSelected ? "#1D4ED8" : "#2563EB"}
                          strokeWidth={isSelected ? 3 : 1.5}
                        />
                        {/* Title text */}
                        <SvgText
                          x={node.size / 2}
                          y={node.size / 2 - 2}
                          textAnchor="middle"
                          fontSize={Math.max(9, Math.min(13, node.size * 0.16))}
                          fontWeight="bold"
                          fill="#1E293B"
                        >
                          {node.label.length > 12 ? `${node.label.slice(0, 10)}…` : node.label}
                        </SvgText>
                        {/* Duration badge */}
                        <SvgText
                          x={node.size / 2}
                          y={node.size / 2 + 13}
                          textAnchor="middle"
                          fontSize={Math.max(8, Math.min(11, node.size * 0.13))}
                          fill="#64748B"
                        >
                          {node.durationDays || 1}d
                        </SvgText>
                      </G>
                    );
                  }

                  // Media, Contact, or Group Dots
                  return (
                    <G
                      key={node.id}
                      transform={`translate(${x}, ${y})`}
                      onPress={() => handleNodePress(node)}
                    >
                      <Circle
                        r={node.size}
                        fill={node.color || "#2563EB"}
                        stroke={isSelected ? "#1D4ED8" : "#FFFFFF"}
                        strokeWidth={isSelected ? 3 : 1.5}
                      />
                      <SvgText
                        x={0}
                        y={node.size + 11}
                        textAnchor="middle"
                        fontSize={9}
                        fill="#334155"
                        fontWeight="500"
                      >
                        {node.label.length > 10 ? `${node.label.slice(0, 8)}…` : node.label}
                      </SvgText>
                    </G>
                  );
                })}
              </G>
            </G>
          </Svg>
        </View>

        {/* 3. Selected Node Detail Inspector Drawer */}
        {selectedNode && (
          <View style={[styles.inspectorDrawer, { backgroundColor: cardBg, borderColor }]}>
            <View style={styles.inspectorHeader}>
              <View style={styles.inspectorTitleRow}>
                <EntityIcon type={selectedNode.type} size={18} color="#2563EB" />
                <ThemedText style={styles.inspectorTitle}>{selectedNode.label}</ThemedText>
                <ThemedText style={styles.inspectorTypeBadge}>
                  {selectedNode.type.toUpperCase()}
                </ThemedText>
              </View>
              <TouchableOpacity
                onPress={() => setSelectedNode(null)}
                style={styles.closeInspectorBtn}
              >
                <Ionicons name="close" size={18} color={textMuted} />
              </TouchableOpacity>
            </View>

            <View style={styles.inspectorBody}>
              {selectedNode.type === "memory" && (
                <View style={styles.inspectorMetaRow}>
                  <ThemedText style={styles.inspectorMetaText}>
                    Date: {(selectedNode.data as MemoryModel).date || "Undated"}
                  </ThemedText>
                  <ThemedText style={styles.inspectorMetaText}>
                    Duration: {selectedNode.durationDays || 1} day(s)
                  </ThemedText>
                  {(selectedNode.data as MemoryModel).media && (
                    <ThemedText style={styles.inspectorMetaText}>
                      Media Highlights: {(selectedNode.data as MemoryModel).media?.length ?? 0} item(s)
                    </ThemedText>
                  )}
                </View>
              )}

              {selectedNode.type === "media" && (
                <View style={styles.inspectorMetaRow}>
                  <ThemedText style={styles.inspectorMetaText}>
                    Favourite Rating: {selectedNode.rating || 1}★ (Size: {selectedNode.size}px)
                  </ThemedText>
                  <ThemedText style={styles.inspectorMetaText}>
                    Type: {(selectedNode.data as MediaModel).contentType || "media/file"}
                  </ThemedText>
                </View>
              )}

              {selectedNode.type === "contact" && (
                <View style={styles.inspectorMetaRow}>
                  <ThemedText style={styles.inspectorMetaText}>
                    Relationship: {(selectedNode.data as any).relationship || "Associate"}
                  </ThemedText>
                  <ThemedText style={styles.inspectorMetaText}>
                    Email: {(selectedNode.data as any).email || "None"}
                  </ThemedText>
                </View>
              )}

              {selectedNode.type === "group" && (
                <View style={styles.inspectorMetaRow}>
                  <ThemedText style={styles.inspectorMetaText}>
                    Members: {(selectedNode.data as any).members?.length || 0}
                  </ThemedText>
                  <ThemedText style={styles.inspectorMetaText}>
                    Description: {(selectedNode.data as any).description || "None"}
                  </ThemedText>
                </View>
              )}
            </View>
          </View>
        )}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    width: "100%",
  },
  exporterToolbar: {
    flexDirection: "row",
    justifyContent: "flex-end",
    marginBottom: 6,
  },
  canvasWrapper: {
    flex: 1,
    minHeight: 520,
    position: "relative",
    borderWidth: 1,
    backgroundColor: "#FAFAF9",
    overflow: "hidden",
  },
  svgContainer: {
    flex: 1,
    width: "100%",
    height: "100%",
  },
  svgElement: {
    flex: 1,
    width: "100%",
    height: "100%",
  },
  controlsOverlay: {
    position: "absolute",
    top: 12,
    right: 12,
    zIndex: 10,
    gap: 6,
  },
  controlBtn: {
    width: 32,
    height: 32,
    borderWidth: 1,
    alignItems: "center",
    justifyContent: "center",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 2,
    elevation: 2,
  },
  legendOverlay: {
    position: "absolute",
    top: 12,
    left: 12,
    zIndex: 10,
    padding: 8,
    borderWidth: 1,
    gap: 4,
    opacity: 0.95,
  },
  legendItem: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
  },
  legendMemorySquare: {
    width: 14,
    height: 14,
    backgroundColor: "#F5EBE1",
    borderColor: "#2563EB",
    borderWidth: 1.5,
  },
  legendMediaDot: {
    width: 12,
    height: 12,
    borderRadius: 6,
    backgroundColor: "#2563EB",
  },
  legendContactDot: {
    width: 10,
    height: 10,
    borderRadius: 5,
    backgroundColor: "#D97706",
  },
  legendGroupDot: {
    width: 12,
    height: 12,
    borderRadius: 6,
    backgroundColor: "#059669",
  },
  legendText: {
    fontSize: 10,
    fontWeight: "500",
    color: "#334155",
  },
  loadingOverlay: {
    position: "absolute",
    bottom: 16,
    left: 16,
    zIndex: 10,
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    backgroundColor: "rgba(255, 255, 255, 0.9)",
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderWidth: 1,
    borderColor: "#D0E1F9",
  },
  loadingText: {
    fontSize: 11,
    color: "#2563EB",
    fontWeight: "600",
  },
  inspectorDrawer: {
    position: "absolute",
    bottom: 12,
    left: 12,
    right: 12,
    zIndex: 20,
    borderWidth: 1,
    padding: 12,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 4,
  },
  inspectorHeader: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 8,
  },
  inspectorTitleRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    flex: 1,
  },
  inspectorTitle: {
    fontSize: 14,
    fontWeight: "700",
    color: "#1E293B",
  },
  inspectorTypeBadge: {
    fontSize: 9,
    fontWeight: "700",
    color: "#2563EB",
    backgroundColor: "#D0E1F9",
    paddingHorizontal: 6,
    paddingVertical: 2,
  },
  closeInspectorBtn: {
    padding: 4,
  },
  inspectorBody: {
    marginTop: 2,
  },
  inspectorMetaRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 16,
  },
  inspectorMetaText: {
    fontSize: 11,
    color: "#475569",
  },
});
